import React, { useEffect, useState } from 'react';
import {
  formatPhoneNumberIntl,
  isPossiblePhoneNumber
} from 'react-phone-number-input';
import { NavLink } from 'react-router-dom';

import { IFormFieldInputBaseProps } from '@components/form-fields';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import { IAbstractRecord } from '@models';
import { Box, Button, Tooltip } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import { MAIN_THEME } from '@styles';
import {
  addZeroesAndSeparatevalue,
  isExternalLink,
  maskValueWithPatternIfMatch,
  parseStringTemplate,
  trimTrailingZeros
} from '@utils/formatting-utils';
import {
  isEmpty,
  isValidDecimalCount,
  isValidNumber
} from '@utils/validationUtils';

import EnhancedFormattedDecimalInput from '../../../_old/form/EnhancedFormattedDecimalInput';
import EnhancedInput from '../../../_old/form/EnhancedInput';
import ToolTip from '../../../tooltip/ToolTip';
import ToolTipTable from '../../../tooltip/ToolTipTable';
import { IEnhancedRow } from './EnhancedTableBody';
import LimitedTextWrapper from './LimitTextWrapper';
import {
  EnhancedHeaderInputTypes,
  EnhancedTableHeader,
  EnhancedTableHeaderType
} from '.';

import clsx from 'clsx';
import DateService from 'src/core/services/DateService';
import FormattingService from 'src/core/services/FormattingService';
import PrecisionService from 'src/core/services/PrecisionService';
import { makeStyles } from 'tss-react/mui';

interface IEnhancedTableBodyCellProps extends IFormFieldInputBaseProps {
  row: IEnhancedRow;
  header: EnhancedTableHeader;
  currencySymbol?: string;
  selected: boolean;
  handleUpdate?(
    rowIndex: number,
    property: string,
    value: any,
    row?: IAbstractRecord
  ): void;
  showCellFullData?: boolean;
  handleCellValueChanged?(
    rowIndex: number,
    columnId: string,
    newValue: any,
    rowId?: string,
    row?: IAbstractRecord
  ): void;
  tableClasses?: {
    inputContainer?: string;
  };
}

const useStyles = makeStyles()(() => ({
  cellRoot: {
    color: `${MAIN_THEME.color.black} !important`,
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '12px !important',
    height: '34px !important',
    border: 'none !important',
    padding: '2px 8px',
    fontFamily: `${MAIN_THEME.font.regular} !important`,
    width: 'var(--col-width, auto)'
  },
  cellLink: {
    color: `${MAIN_THEME.color.black} !important`,
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    fontFamily: MAIN_THEME.font.regular
  },
  cellbutton: {
    textAlign: 'left',
    color: MAIN_THEME.color.black,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '12px',
    height: 34,
    padding: 0,
    fontFamily: MAIN_THEME.font.regular
  },
  disabledButton: {
    cursor: 'not-allowed'
  },
  cellBadge: {
    borderRadius: '5px',
    padding: '5px',
    display: 'inline-block',
    minWidth: '50px',
    textAlign: 'center'
  },
  classificationContainer: {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    padding: '0 5px 0 0'
  },
  icon: {
    minHeight: '15px',
    minWidth: '15px',
    borderRadius: '50%',
    margin: '0 7px 0 0',
    verticalAlign: 'middle',
    display: 'inline-block'
  },
  classificationInput: {
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  input: {
    width: '90%',
    maxWidth: '200px',
    border: `1px solid ${MAIN_THEME.color.white92}`,
    height: '28px',
    margin: '0 auto',
    fontSize: MAIN_THEME.fontSize.sm,
    boxSizing: 'border-box',
    lineHeight: '15px',
    borderRadius: '5px',
    backgroundColor: 'white',
    minHeight: '28px !important',
    fontFamily: MAIN_THEME.font.regular,
    '& .MuiInputBase-root': {
      minHeight: '28px !important'
    }
  },
  editableInput: {
    width: '100px',
    maxWidth: '100px',
    border: `1px solid ${MAIN_THEME.color.white92}`,
    height: '28px',
    margin: '0 auto',
    fontSize: MAIN_THEME.fontSize.sm,
    boxSizing: 'border-box',
    lineHeight: '15px',
    borderRadius: '5px',
    backgroundColor: 'white',
    fontFamily: MAIN_THEME.font.regular
  },
  underlined: {
    textDecoration: 'underline'
  },
  positiveCurrency: {
    color: MAIN_THEME.color.green
  },
  negativeCurrency: {
    color: MAIN_THEME.color.red
  },
  svgClickableButton: {
    height: 'auto',
    padding: 3,
    boxShadow: `0 0 3px ${MAIN_THEME.color.white65}`,
    borderRadius: 4,
    transition: '125ms',
    '&:hover': {
      boxShadow: 'none'
    }
  },
  svgClickableIcon: {
    width: '15px',
    height: '15px',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    margin: 0,
    '@media only screen and (max-width: 650px)': {
      width: '12px',
      height: '12px'
    }
  },
  inputContainer: {
    width: '100%',
    maxWidth: '200px',
    height: '30px !important',
    maxHeight: '30px',
    margin: '0 0 0 -4px !important',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      maxHeight: '30px !important'
    }
  },
  inputCellWrapper: {
    position: 'relative',
    width: '100%'
  },
  inputCellIndicators: {
    position: 'absolute',
    right: -8,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    pointerEvents: 'auto'
  },
  validationIndicator: {
    color: '#d32f2f',
    fontWeight: 'bold',
    flexShrink: 0,
    cursor: 'default'
  },
  changedIndicator: {
    color: '#ed6c02',
    fontWeight: 'bold',
    flexShrink: 0
  }
}));

const EnhancedTableBodyCell: React.FC<IEnhancedTableBodyCellProps> = ({
  row,
  header,
  className,
  selected,
  handleUpdate = () => {},
  showCellFullData,
  handleCellValueChanged = () => {},
  tableClasses
}) => {
  const { classes } = useStyles();
  const [currentValue, setCurrentValue] = useState<any>();
  const currencySymbol = '$';
  const phoneNumberPattern = '';

  useEffect(() => {
    const columnValue = row.columns[header.name];
    const normalizedColumnValue =
      typeof columnValue === 'number' && Number.isNaN(columnValue)
        ? ''
        : columnValue;

    setCurrentValue((previousValue: unknown) => {
      if (typeof previousValue === 'string') {
        if (
          previousValue === '-' ||
          previousValue === '-.' ||
          previousValue.endsWith('.')
        ) {
          return previousValue;
        }
      }

      return normalizedColumnValue;
    });
  }, [header.name, row.columns]);

  function renderCellContents(): JSX.Element | string {
    switch (header.type) {
      case EnhancedTableHeaderType.Link: {
        return !isEmpty(row.columns[header.name]) &&
          isExternalLink(row.columns[header.name]) !== null ? (
          <button
            type="button"
            onClick={() => {
              window.open(row.columns[header.name]);
            }}
          >
            <LimitedTextWrapper
              text={row.columns[header.name]}
              maxCharacter={40}
            />
          </button>
        ) : (
          <NavLink
            className={classes.cellLink}
            target={header.openUrlInNewTab ? '_blank' : undefined}
            to={parseStringTemplate(
              header.urlTemplate,
              row.columns.id,
              row.columns
            )}
            // exact
          >
            <button className={classes.cellbutton}>
              {row.columns[header.name]}
            </button>
          </NavLink>
        );
      }
      case EnhancedTableHeaderType.Date:
        return DateService.formatDate(row.columns[header.name]);
      case EnhancedTableHeaderType.DateAndTime:
        return DateService.formatDateTime(row.columns[header.name]);
      case EnhancedTableHeaderType.Clickable:
        if (header.icon) {
          return (
            <Button onClick={() => header.callback(row)}>{header.icon}</Button>
          );
        } else {
          const cellValue = row.columns[header.name];
          const displayValue = header.buttonLabel || cellValue;

          if (!displayValue || displayValue === '-') {
            return '-';
          }

          return (
            <button
              className={classes.cellbutton}
              type="button"
              onClick={() => header.callback(row)}
            >
              {displayValue}
            </button>
          );
        }

      case EnhancedTableHeaderType.ColoredText:
        return (
          <span
            style={{
              color:
                header.textColorConfig[row.columns[header.name].toString()],
              fontWeight: 'bold'
            }}
          >
            {row.columns[header.name]}
          </span>
        );

      case EnhancedTableHeaderType.Badge:
        return (
          <span
            className={classes.cellBadge}
            style={{
              color:
                header.badgeConfig[row.columns[header.name].toString()].color,
              backgroundColor:
                header.badgeConfig[row.columns[header.name]].backgroundColor
            }}
          >
            {row.columns[header.name]}
          </span>
        );
      case EnhancedTableHeaderType.BadgeArray: {
        const values = row.columns[header.name] as string[];

        return (
          <div>
            {values.map((v, index) => {
              const isLastIndex = values.length === index + 1;

              return (
                <span
                  className={classes.cellBadge}
                  key={v}
                  style={{
                    color: header.badgeConfig[v.toString()].color,
                    backgroundColor: header.badgeConfig[v].backgroundColor,
                    marginRight: !isLastIndex ? '5px' : 0
                  }}
                >
                  {v}
                </span>
              );
            })}
          </div>
        );
      }
      case EnhancedTableHeaderType.Currency: {
        if (!isValidNumber(row.columns[header.name])) {
          return row.columns[header.name];
        }

        const currency =
          header.currency || row.columns['Currency'] || currencySymbol;

        const value = Number(row.columns[header.name]);
        const displayValue = FormattingService.formatCustomCurrency(
          Math.abs(value),
          currency
        );
        const isPostive = value >= 0;
        if (isPostive) {
          return displayValue;
        }

        return <>{`(${displayValue})`}</>;
      }
      case EnhancedTableHeaderType.RoundedCurrency: {
        const currency =
          header.currency ||
          row.columns['Currency'] ||
          row.columns[`${header.name}Currency`] ||
          currencySymbol;

        if (!isValidNumber(row.columns[header.name])) {
          return `${currency} 0`;
        }
        const value = Number(row.columns[header.name]);
        const displayValue = FormattingService.formatCustomCurrency(
          Math.abs(value),
          currency,
          3,
          true
        );
        const isPostive = value >= 0;
        if (isPostive) {
          return displayValue;
        }

        return <>{`(${displayValue})`}</>;
      }
      case EnhancedTableHeaderType.ColoredCurrency: {
        if (!isValidNumber(row.columns[header.name])) {
          return row.columns[header.name];
        }

        const currency =
          header.currency || row.columns['Currency'] || currencySymbol;

        const value = Number(row.columns[header.name]);
        const displayValue = FormattingService.formatCustomCurrency(
          Math.abs(value),
          currency
        );
        const isPostive = value >= 0;
        if (isPostive) {
          return (
            <span className={classes.positiveCurrency}>{displayValue}</span>
          );
        }

        return (
          <span className={classes.negativeCurrency}>
            {`(${displayValue})`}
          </span>
        );
      }
      case EnhancedTableHeaderType.Percentage:
        return isValidNumber(row.columns[header.name])
          ? `${addZeroesAndSeparatevalue(row.columns[header.name])} %`
          : row.columns[header.name];
      case EnhancedTableHeaderType.RoundedPercentage:
        return isValidNumber(row.columns[header.name])
          ? `${trimTrailingZeros(addZeroesAndSeparatevalue(row.columns[header.name]))} %`
          : row.columns[header.name];
      case EnhancedTableHeaderType.Rate:
        return isValidNumber(row.columns[header.name])
          ? `${PrecisionService.multiplyBy100(row.columns[header.name])} %`
          : row.columns[header.name];

      case EnhancedTableHeaderType.Classification:
        return header.classificationPropertyName ? (
          <div className={classes.classificationContainer}>
            <figure
              className={classes.icon}
              style={{
                backgroundColor: row.columns[header.classificationPropertyName]
              }}
            />
            <span
              className={classes.classificationInput}
              style={{ color: row.columns[header.classificationPropertyName] }}
            >
              {row.columns[header.name]}
            </span>
          </div>
        ) : (
          row.columns[header.name]
        );

      case EnhancedTableHeaderType.PhoneNumber: {
        const value =
          row.columns[header.name] && phoneNumberPattern
            ? maskValueWithPatternIfMatch(
                row.columns[header.name],
                phoneNumberPattern
              )
            : row.columns[header.name];

        return <LimitedTextWrapper text={value} maxCharacter={30} />;
      }
      case EnhancedTableHeaderType.IntPhoneNumber: {
        let value = row.columns[header.name];
        if (value && isPossiblePhoneNumber(value)) {
          value = formatPhoneNumberIntl(value);
        }

        return <LimitedTextWrapper text={value} maxCharacter={30} />;
      }
      case EnhancedTableHeaderType.Image: {
        const value = row.columns[header.name];

        return header.hasConditionalImage ? (
          value.toLowerCase() === 'active' ? (
            <Tooltip title={value}>
              <img
                src={header.variableOneImageKey}
                alt=""
                width={14}
                height={14}
              />
            </Tooltip>
          ) : (
            <Tooltip title={value}>
              <img
                src={header.variableTwoImageKey}
                alt=""
                width={14}
                height={14}
              />
            </Tooltip>
          )
        ) : (
          <img src={header.imageKey} alt="" width={20} height={20} />
        );
      }
      case EnhancedTableHeaderType.JSXElement: {
        return row.columns[header.name];
      }
      case EnhancedTableHeaderType.Enum: {
        return FormattingService.formatEnumToDisplay(row.columns[header.name]);
      }
      case EnhancedTableHeaderType.Input: {
        const isDisabled =
          typeof header.disabled === 'function'
            ? header.disabled(row)
            : header.disabled || row.columns?.disabled;
        const isChanged =
          row.columns.changedFields instanceof Set &&
          (row.columns.changedFields as Set<string>).has(header.name);
        const fieldValidationErrors = row.columns.fieldValidationErrors as
          | Record<string, string>
          | undefined;
        const roundedCurrencyMaxValue =
          row.columns[`${header.name}MaxValue`] ??
          row.columns['maxValue'] ??
          header.maxValue;
        const roundedCurrencyMinValue =
          row.columns[`${header.name}MinValue`] ??
          row.columns['minValue'] ??
          header.minValue;
        const inlineRoundedCurrencyError =
          header.inputType === EnhancedHeaderInputTypes.RoundedCurrency &&
          !isEmpty(currentValue) &&
          (Number(currentValue) > Number(roundedCurrencyMaxValue) ||
            Number(currentValue) < Number(roundedCurrencyMinValue))
            ? 'Invalid value'
            : undefined;
        const fieldValidationError =
          fieldValidationErrors?.[header.name] ?? inlineRoundedCurrencyError;
        const inputElement = (() => {
          switch (header.inputType) {
            case EnhancedHeaderInputTypes.Decimal: {
              return (
                <>
                  <EnhancedFormattedDecimalInput
                    title=""
                    name=""
                    inline
                    onChange={(e) => {
                      const { value } = e.target;
                      if (
                        isEmpty(value) ||
                        isValidDecimalCount(Number(value), 10)
                      ) {
                        setCurrentValue(e.target.value);
                        handleCellValueChanged(row.index, header.name, value);
                      }
                    }}
                    onBlur={() => {
                      const value = !isEmpty(currentValue)
                        ? Number(currentValue)
                        : '';

                      handleUpdate(row.index, header.name, value);
                    }}
                    value={currentValue ?? ''}
                    error={header.error || row.columns.error}
                    disabled={isDisabled}
                    placeholder={header.title}
                    className={classes.editableInput}
                    isEditable={true}
                    minValue={header.minValue}
                    maxValue={header.maxValue}
                  />
                </>
              );
            }
            case EnhancedHeaderInputTypes.Text: {
              return (
                <EnhancedInput
                  title=""
                  name=""
                  inline
                  type="text"
                  onChange={(e) => {
                    setCurrentValue(e.target.value);
                    handleCellValueChanged(
                      row.index,
                      header.name,
                      e.target.value
                    );
                  }}
                  onBlur={() => {
                    handleUpdate(row.index, header.name, currentValue);
                  }}
                  value={currentValue ?? ''}
                  error={header.error || row.columns.error}
                  hideError={false}
                  disabled={isDisabled}
                  placeholder={header.title}
                />
              );
            }
            case EnhancedHeaderInputTypes.RoundedCurrency: {
              const maxValue =
                row.columns[`${header.name}MaxValue`] ??
                row.columns['maxValue'] ??
                header.maxValue;
              const minValue =
                row.columns[`${header.name}MinValue`] ??
                row.columns['minValue'] ??
                header.minValue;

              return (
                <CurrencyFormField
                  name={header.name + row.index}
                  title=""
                  inputOnly
                  hideError
                  value={currentValue ?? ''}
                  maxDecimalPrecision={header.maxDecimalPercision}
                  currencySymbol={
                    header.currency || row.columns['Currency'] || currencySymbol
                  }
                  onChange={(e) => {
                    setCurrentValue(e.target.value);
                    handleCellValueChanged(
                      row.index,
                      header.name,
                      e.target.value,
                      row.index.toString(),
                      row.columns
                    );
                  }}
                  onBlur={() => {
                    handleUpdate(
                      row.index,
                      header.name,
                      currentValue,
                      row.columns
                    );
                  }}
                  allowNegative={
                    row.columns['allowNegative'] || header.allowNegative
                  }
                  minValue={minValue}
                  maxValue={maxValue}
                  disabled={isDisabled}
                  error={
                    header.error ||
                    row.columns.error ||
                    currentValue > maxValue ||
                    currentValue < minValue
                      ? 'Invalid value'
                      : undefined
                  }
                  placeholder={header.title}
                  classes={{
                    inputContainer: classes.inputContainer
                  }}
                />
              );
            }
            case EnhancedHeaderInputTypes.Date: {
              return (
                <DatePickerFormField
                  title=""
                  name=""
                  inputOnly
                  onDateChange={(value) => {
                    setCurrentValue(value);
                    handleCellValueChanged(row.index, header.name, value);
                  }}
                  value={currentValue ?? ''}
                  error={header.error || row.columns.error}
                  maxDate={header.maxDate}
                  minDate={header.minDate}
                  disabled={isDisabled}
                  placeholder={header.title}
                  hideError
                  classes={{
                    inputContainer: classes.inputContainer
                  }}
                />
              );
            }
            case EnhancedHeaderInputTypes.Select: {
              return (
                <SelectFormField
                  title=""
                  name=""
                  inputOnly
                  onChange={(value) => {
                    setCurrentValue(value);
                    handleCellValueChanged(
                      row.index,
                      header.name,
                      value,
                      row.key
                    );
                  }}
                  onBlur={() => {
                    handleUpdate(row.index, header.name, currentValue);
                  }}
                  value={currentValue ?? ''}
                  error={header.error || row.columns.error}
                  hideError
                  disabled={isDisabled}
                  placeholder={header?.placeholder ?? header?.title}
                  selectOptions={header.options || {}}
                  classes={{
                    input: clsx(
                      // classes.inputContainer,
                      tableClasses?.inputContainer
                    )
                  }}
                />
              );
            }
            default:
              return null;
          }
        })();

        return (
          <Box className={classes.inputCellWrapper}>
            {inputElement}
            {(fieldValidationError || isChanged) && (
              <Box className={classes.inputCellIndicators}>
                {fieldValidationError && (
                  <Tooltip title={fieldValidationError}>
                    <span className={classes.validationIndicator}>!</span>
                  </Tooltip>
                )}
                {isChanged && (
                  <span
                    title="This field has been modified"
                    className={classes.changedIndicator}
                  >
                    !
                  </span>
                )}
              </Box>
            )}
          </Box>
        );
      }
      // eslint-disable-next-line no-fallthrough
      default:
        return (
          <LimitedTextWrapper
            text={row.columns[header.name]}
            maxCharacter={showCellFullData ? 300 : 80}
          />
        );
    }
  }

  const renderToolTipsWrapper = () => {
    if (header.tooltipSettings && row.columns.id) {
      const allHeaders: Record<string, EnhancedTableHeader> =
        header.tooltipSettings.headers;

      return (
        <ToolTip
          displayContent={
            <span className={classes.cellbutton}> {renderCellContents()} </span>
          }
        >
          <ToolTipTable
            headers={allHeaders}
            rowId={row.columns.id}
            tooltipFetch={header.tooltipSettings.tooltipFetch}
          />
        </ToolTip>
      );
    }

    return renderCellContents();
  };

  return (
    <TableCell
      style={
        {
          '--col-width': header.width
        } as React.CSSProperties
      }
      component="th"
      scope="row"
      padding="none"
      classes={{ root: classes.cellRoot }}
      className={className}
    >
      {renderToolTipsWrapper()}
    </TableCell>
  );
};

export default EnhancedTableBodyCell;
